import gsap from 'gsap';
import Draggable from 'gsap/Draggable';
import difference from 'lodash/difference';
import {shuffle} from '@kids/lib/transform/array';

gsap.registerPlugin(Draggable);

export default {
    data: () => ({
        sortKeys: [],
        blockedIndices: [],
        sortItemCount: 0,
        shuffleSortItems: false
    }),
    created()
    {
        this._dragKeys = [];
        this._positions = [];
        this._currentDragged = null;

        if (this.sortKeys.length === 0 && this.sortItemCount > 0)
        {
            const sortKeys = [];
            for (let i = 0; i < this.sortItemCount; i++)
            {
                sortKeys.push(i);
            }
            if (this.shuffleSortItems)
            {
                do
                {
                    shuffle(sortKeys);
                }
                while (
                    sortKeys.every((key, index) => key === index)
                );
            }
            this.sortKeys = sortKeys;
        }
    },
    mounted()
    {
        const {onDrag, onPress, onRelease} = this;
        const {sortables} = this.$refs;

        this._sortables = Draggable.create(sortables, {
            onDrag,
            onPress,
            onRelease,
            zIndexBoost: false,
            sortKey: 0,
            top: 0,
            bottom: 0,
            left: 0
        });

        this._sortables.forEach((sortable, index) =>
        {
            if (this.blockedIndices.includes(index))
            {
                sortable.disable();
            }
            sortable.vars.sortKey = this.sortKeys[index];
        });
    },
    destroyed()
    {
        if (this._sortables)
        {
            this._sortables.forEach(sortable => sortable.kill());
        }
    },
    methods: {
        updatePositions()
        {
            this._positions.length = 0;
            this._sortables.forEach((sortable) =>
            {
                const rect = sortable.target.getBoundingClientRect();
                const {vars} = sortable;

                vars.top = rect.top;
                vars.bottom = rect.top + rect.height;
                vars.left = rect.left;

                this._positions.push({
                    top: vars.top,
                    bottom: vars.bottom,
                    left: vars.left
                });
            });
            this._positions.sort((p1, p2) => p1.top - p2.top);
        },
        onPress(evt)
        {
            const path = evt.composedPath();
            let target = null;

            this.updatePositions();

            do
            {
                target = path.shift();
                this._currentDragged = Draggable.get(target);
            }
            while (!this._currentDragged);

            this._dragKeys = [...this.sortKeys];

            gsap.to(target, {
                duration: 0.2,
                opacity: 0.75,
                scale: 0.95,
                rotate: 1.5,
                zIndex: '+=1000'
            });
        },
        onDrag()
        {
            const currentDragged = this._currentDragged;
            const {sortKey} = currentDragged.vars;
            const bottom = currentDragged.y + currentDragged.vars.bottom;
            const top = currentDragged.y + currentDragged.vars.top;
            const {deltaY} = currentDragged;

            if (deltaY === 0) { return; }

            const currentIndex = this.getCurrentIndex(top, bottom, deltaY > 0);
            if (
                currentIndex === -1 ||
                this._dragKeys[currentIndex] === sortKey ||
                this.blockedIndices.includes(currentIndex)
            )
            {
                return;
            }
            this._dragKeys.length = 0;
            this._dragKeys[currentIndex] = sortKey;
            this.blockedIndices.forEach((index) =>
            {
                this._dragKeys[index] = this.sortKeys[index];
            });
            const valuesToFill = difference(this.sortKeys, this._dragKeys);
            this.sortKeys.forEach((value, index) =>
            {
                if (this._dragKeys[index] === undefined)
                {
                    this._dragKeys[index] = valuesToFill.shift();
                }
            });

            this.repositionSortables();
        },
        repositionSortables()
        {
            const currentDragged = this._currentDragged;
            this._dragKeys.forEach((sortKey, i) =>
            {
                if (sortKey === currentDragged.vars.sortKey || this.blockedIndices.includes(i))
                {
                    return;
                }
                const position = this._positions[i];
                const sortable = this.findSortableByKey(sortKey);
                gsap.to(sortable.target, {
                    duration: 0.2,
                    y: position.top - sortable.vars.top
                });
            });
        },
        getCurrentIndex(top, bottom, draggingDown)
        {
            const positions = this._positions;
            const {length} = positions;

            if (bottom < positions[1].top)
            {
                return 0;
            }

            if (top > positions[length - 1].bottom)
            {
                return length - 1;
            }

            for (
                let i = draggingDown ? length - 1 : 0;
                draggingDown ? (i >= 0) : (i < length);
                draggingDown ? (i--) : (i++)
            )
            {
                const position = positions[i];
                if (
                    (bottom >= position.top && bottom <= position.bottom) ||
                    (top >= position.top && top <= position.bottom)
                )
                {
                    return i;
                }
            }

            return -1;
        },
        onRelease()
        {
            const currentDragged = this._currentDragged;
            const {sortKey} = currentDragged.vars;
            const {target} = currentDragged;
            const index = this._dragKeys.indexOf(sortKey);
            const position = this._positions[index];
            const toVars = {};
            const event = {sortKey, index, toVars, draggable: currentDragged};

            this.dropHandler(event);
            delete event.toVars;
            delete event.draggable;

            gsap.to(target, {
                opacity: 1,
                scale: 1,
                rotate: 0,
                ...toVars,
                duration: 0.3,
                y: position.top - currentDragged.vars.top,
                x: position.left - currentDragged.vars.left,
                onComplete: () =>
                {
                    this.$audioController.play('drop', {volume: 0.5});
                    this.sortKeys = [...this._dragKeys];
                    this.$nextTick(this.resetSortables);
                    this.$nextTick(() => this.sortingCompleteHandler(event));
                }
            });

            this.disableSortables();

            this._currentDragged = null;
        },
        sortingCompleteHandler()
        {

        },
        dropHandler()
        {

        },
        findSortableByKey(sortKey)
        {
            return this._sortables.find(sortable => sortable.vars.sortKey === sortKey);
        },
        disableSortables()
        {
            this._sortables.forEach(sortable => sortable.disable());
        },
        enableSortables()
        {
            this._sortables.forEach((sortable, index) =>
            {
                if (this.blockedIndices.includes(index)) { return; }
                sortable.enable();
            });
        },
        resetSortables()
        {
            gsap.set(
                this._sortables.map(sortable => sortable.target),
                {clearProps: 'all'}
            );
            this.$refs.sortables.forEach((target, index) =>
            {
                const sortKey = this.sortKeys[index];
                Draggable.get(target).vars.sortKey = sortKey;
            });
            this.enableSortables();
        },
        blockSortable(index)
        {
            if (!this.blockedIndices.includes(index))
            {
                this.blockedIndices.push(index);
                this.findSortableByKey(this.sortKeys[index]).disable();
            }
        }
    }
};
